import { DataSource } from '@angular/cdk/collections';
import { ListLogsResponse, Log, LoggerApiService } from '@vendasta/wsp-logger';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// DEFAULT_PAGE_SIZE denotes the default number of logs to return upon a request
// note that this is only used at the initialization step of the service, and is
// adjusted as the data source is used
const DEFAULT_PAGE_SIZE = 10;

// PageOptions denotes a collection of parameters that control the paging
// behaviour of the data source
interface PageOptions {
  prevIndex: number;
  index: number;
  size: number;
}

/**
 * ErrorLogsDataSource implements a data source for the PHP error log model
 */
export class ErrorLogsDataSource extends DataSource<Log> {
  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private logs$$: ReplaySubject<Log[]> = new ReplaySubject<Log[]>(1);
  // the logsCount behaviour subject is used for asynchronously checking whether
  // the number of logs is 0, and displaying a user warning accordingly. Due to
  // mat pagination not supporting async objects for their length property, this
  // is used in combination with the sync logCount variable
  private logsCount$$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private pageOptions$$: BehaviorSubject<PageOptions> = new BehaviorSubject<PageOptions>({
    prevIndex: 0,
    index: 0,
    size: DEFAULT_PAGE_SIZE,
  });
  private isStaging$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  loading$: Observable<boolean> = this.loading$$.asObservable();
  logs$: Observable<Log[]> = this.logs$$.asObservable();
  logsCount$: Observable<number> = this.logsCount$$.asObservable();
  searchTerm$: Observable<string> = this.searchTerm$$.asObservable();
  pageOptions$: Observable<PageOptions> = this.pageOptions$$.asObservable();
  isStaging$: Observable<boolean> = this.isStaging$$.asObservable();

  // used by getLogByTimestamp to return a log
  private log$$: ReplaySubject<Log[]> = new ReplaySubject<Log[]>();
  log$: Observable<Log[]> = this.log$$.asObservable();

  // logsCount is used for passing the number of logs to the material paginator
  // used by the error logs HTML template, which cannot take in an observable
  logsCount = 0;

  // cursors holds a mapping from the index of a page to the corresponding cursor. Because
  // the PHP error log API requires a hash cursor string, we need to keep track of which
  // one to use on each page index in order to get the correct logs from the API
  cursors: Record<number, string> = {
    0: '',
  };

  // a boolean indicator to suggest whether the user has received the maximum number of logs. This is required in order
  // to keep track of updating the total number of logs displayed by the paginator of the table subscribing
  // to this data source
  reachedMax = false;

  constructor(
    private site_id: string,
    private loggerService: LoggerApiService,
    private snowplow: ProductAnalyticsService,
  ) {
    super();
  }

  /**
   * Monitors the page option events, which are used for paginating over error logs
   * @param event - PageEvent containing the fields for pagination
   */
  updatePageOptions(event: PageEvent): void {
    this.logs$$.next([]);
    this.pageOptions$$.next({
      prevIndex: event.previousPageIndex,
      index: event.pageIndex,
      size: event.pageSize,
    });
  }

  /**
   * Updates the search term used for searching error logs based on the content of the error
   * @param term - string to search for
   */
  updateSearchTerm(term: string): void {
    this.searchTerm$$.next(term);
  }

  /**
   * Updates the state of the data source with an indicator of whether to get
   * PHP error logs for the staging site
   * @param isStaging - indicator of whether to return staging logs or not
   */
  updateLogsType(isStaging: boolean): void {
    this.logs$$.next([]);
    this.logsCount = 0;
    this.logsCount$$.next(this.logsCount);
    this.isStaging$$.next(isStaging);
  }

  /**
   * Refreshes all the logs by resetting the parameters to their default state
   * such that the data source will contain new logs
   */
  refresh(): void {
    this.logs$$.next([]);
    this.logsCount = 0;
    this.logsCount$$.next(this.logsCount);
    this.searchTerm$$.next('');
    this.pageOptions$$.next({
      prevIndex: 0,
      index: 0,
      size: DEFAULT_PAGE_SIZE,
    });
  }

  /**
   * Connects this data source to an API that provides the PHP error logs
   */
  connect(): Observable<Log[] | ReadonlyArray<Log>> {
    combineLatest([this.searchTerm$, this.pageOptions$, this.isStaging$])
      .pipe(
        tap(() => this.loading$$.next(true)),
        debounceTime(300),
        switchMap(([searchTerm, pageOptions, isStaging]: [string, PageOptions, boolean]) => {
          return this.loggerService.listLogs({
            siteId: this.site_id,
            staging: isStaging,
            searchTerm: searchTerm,
            pagingOptions: {
              cursor: this.cursors[pageOptions.index],
              pageSize: pageOptions.size,
            },
          });
        }),
      )
      .subscribe((resp: ListLogsResponse) => {
        this.logs$$.next(resp.logs);
        // the cursor is stored for the next page index in case the user hits "next page". When a request
        // for the previous page comes in, the cursor of that index will be used
        // e.g (index -> cursor) 0 -> '', 1 -> 'some_hash_1', 2 -> 'some_hash_2'
        this.cursors[this.pageOptions$$.getValue().index + 1] = resp.pagingMetadata.nextCursor;

        this.loading$$.next(false);
        if (resp.logs) {
          // the PHP error logs API does not return the total number of logs, so this artificially
          // inflates the logs count based on the passed size. Note that this does not have to worry about the
          // next cursor, even if it is set a few lines above, as the "next" option will not be available
          // due to the following check
          const pageSize = this.pageOptions$$.getValue().size;
          const prevPageIndex = this.pageOptions$$.getValue().prevIndex;
          const pageIndex = this.pageOptions$$.getValue().index;
          if (!this.reachedMax) {
            if (resp.logs.length < pageSize) {
              // subtract the previously added pageSize  when reaching the last page i.e length logs < requested page size
              this.logsCount = this.logsCount + resp.logs.length - pageSize;
              this.reachedMax = true;
            } else if (prevPageIndex <= pageIndex) {
              // top up logs count in order to make the mat paginator of the data source subscribing client
              // know that there's more data to load
              this.logsCount = this.logsCount + resp.logs.length + pageSize * (pageIndex + 1);
            }
          }
          this.logsCount$$.next(this.logsCount);
        } else {
          this.logsCount = 0;
        }
      });
    return this.logs$;
  }

  getLogByTimestamp(timestamp: string): Observable<Log[] | ReadonlyArray<Log>> {
    this.isStaging$
      .pipe(
        switchMap((isStaging: boolean) => {
          return this.loggerService.getLogByTimestamp({
            siteId: this.site_id,
            staging: isStaging,
            timestamp: timestamp,
          });
        }),
      )
      .subscribe((resp: ListLogsResponse) => {
        this.log$$.next(resp.logs);
      });
    return this.log$;
  }

  /**
   * Disconnects the data source
   */
  disconnect(): void {
    return;
  }
}
